import FaIcon from '@components/FaIcon';
import MDBox from '@components/MDBox';
import MDInput from '@components/MDInput';
import MDTypography from '@components/MDTypography';
import { SUBMITTED_GOOGLE_SHEET_LINK } from '@constants/eventNames';
import {
  INVALID_URL_OR_SPREADSHEET_ID,
  NON_GOOGLE_SHEET_RESOURCE_URL
} from '@constants/messages';
import { FormHelperText, Grid, Tooltip } from '@mui/material';
import { sendAmplitudeData } from '@utils/amplitude';
import { TooltipProps, styled, tooltipClasses } from '@mui/material';
import { FC, useState } from 'react';
import googleSheetAccess from '@assets/images/Google_Sheet_Access.png';

interface UploadSpreadsheetProps {
  startUpload: boolean;
  handleGoogleSheetUpload: (spreadsheetLink: string) => Promise<void>;
  setIsValidSpreadsheet: React.Dispatch<React.SetStateAction<boolean>>;
  setSpreadsheetLinkForUpload: React.Dispatch<React.SetStateAction<string>>;
  isDisabled: boolean;
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.shadows[4]
  }
}));

const UploadSpreadsheet: FC<UploadSpreadsheetProps> = ({
  startUpload,
  handleGoogleSheetUpload,
  setIsValidSpreadsheet,
  setSpreadsheetLinkForUpload,
  isDisabled
}) => {
  const [spreadsheetLink, setSpreadsheetLink] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleSubmitSpreadsheet = () => {
    sendAmplitudeData(SUBMITTED_GOOGLE_SHEET_LINK);
    const spreadsheetLinkRegex =
      /https:\/\/docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9_-]{44}\/?(edit#gid=0|edit\?usp=sharing)?\/?/;

    const xlsxSheetLinkRegex =
      /https:\/\/docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9_-]{33}\/?(edit#gid=0|edit\?usp=sharing)?\/?/;

    const spreadsheetIdRegex = /^[a-zA-Z0-9-_]{44}$/;

    if (
      spreadsheetLinkRegex.test(spreadsheetLink) ||
      spreadsheetIdRegex.test(spreadsheetLink) ||
      xlsxSheetLinkRegex.test(spreadsheetLink)
    ) {
      setSpreadsheetLinkForUpload(spreadsheetLink);

      setIsValidSpreadsheet(true);
      setErrorMessage('');
      handleGoogleSheetUpload(spreadsheetLink);
    } else {
      const googleDocsRegex = /https:\/\/docs\.google\.com\/document\/d\/\w+/;
      const googleSlidesRegex =
        /https:\/\/docs\.google\.com\/presentation\/d\/\w+/;
      const googleDriveRegex =
        /https:\/\/drive\.google\.com\/drive\/folders\/\w+/;
      const googleFormsRegex = /https:\/\/docs\.google\.com\/forms\/d\/\w+/;

      [
        googleDocsRegex,
        googleDriveRegex,
        googleFormsRegex,
        googleSlidesRegex
      ].forEach((regEx: RegExp) => {
        if (regEx.test(spreadsheetLink))
          setErrorMessage(NON_GOOGLE_SHEET_RESOURCE_URL);
      });
      setErrorMessage((prevMessage) =>
        !prevMessage ? INVALID_URL_OR_SPREADSHEET_ID : prevMessage
      );
    }
  };

  const handleEnterKeyPressForSpreadsheet = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter') handleSubmitSpreadsheet();
  };

  return (
    <MDBox width={'100%'}>
      <Grid
        container
        display='flex'
        flexDirection='row'
        gap={1}
        alignItems='center'
      >
        <MDTypography
          variant='h6'
          color='dark'
          sx={{ my: '15px', opacity: isDisabled ? 0.5 : 1 }}
        >
          Connect your Google Sheet (copy + paste link)
        </MDTypography>
        <LightTooltip
          title={
            <MDBox
              component='img'
              src={googleSheetAccess}
              alt='Brand'
              sx={{
                height: 250,
                width: 250
              }}
            />
          }
          placement='right'
          arrow={false}
        >
          <span>
            <FaIcon
              icon='circle-info'
              size='xs'
              typographyProps={{
                color: 'primary'
              }}
            />
          </span>
        </LightTooltip>
      </Grid>
      <MDInput
        value={spreadsheetLink}
        disabled={startUpload || isDisabled}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setSpreadsheetLink(event.target.value);
          setErrorMessage('');
        }}
        onClickEnter={handleSubmitSpreadsheet}
        onKeyDown={handleEnterKeyPressForSpreadsheet}
        sx={{
          width: '100%',
          mb: errorMessage ? '10px' : '20px',
          '& input': {
            '&::placeholder': {
              fontSize: '0.75rem'
            },
            caretColor: startUpload ? 'transparent' : 'black'
          }
        }}
        needEnterIcon={!errorMessage}
        error={!!errorMessage}
        placeholder="Set your G-sheet's share to Anyone with the link"
      />
      {!!errorMessage && (
        <FormHelperText error sx={{ mb: '10px' }}>
          {errorMessage}
        </FormHelperText>
      )}
    </MDBox>
  );
};

export default UploadSpreadsheet;
